import React, { useEffect, useMemo, useRef } from "react";
import {
  createChart,
  LineSeries,
  AreaSeries,
  CandlestickSeries,
  HistogramSeries,
  CrosshairMode,
} from "lightweight-charts";

/**
 * InteractiveChart — bundled lightweight-charts panel.
 * No runtime third-party network calls.
 *
 * Props:
 *   bars: [{date, open, high, low, close, volume}]  (ascending)
 *   chartType: 'candles' | 'line'    (default 'candles')
 *   height: chart height in px (default 320)
 *   pinnedTime: string | null  — draws a persistent horizontal price line at
 *     the close of this bar (matched by date). null clears any pin.
 *   onCrosshairChange: (bar | null) => void
 *   onBarClick: (bar | null) => void  — fires when user clicks/taps a bar.
 *     `bar` is null if the click landed outside any bar timestamp.
 */
export default function InteractiveChart({
  bars = [],
  chartType = "candles",
  height = 320,
  pinnedTime = null,
  onCrosshairChange,
  onBarClick,
}) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const priceSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const priceLineRef = useRef(null);
  const roRef = useRef(null);
  const barsMapRef = useRef(new Map());
  const clickCbRef = useRef(onBarClick);
  clickCbRef.current = onBarClick;

  const emit = useMemo(() => {
    return (bar) => {
      if (typeof onCrosshairChange !== "function") return;
      onCrosshairChange(bar);
    };
  }, [onCrosshairChange]);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;

    const chart = createChart(el, {
      width: el.clientWidth,
      height,
      autoSize: false,
      layout: {
        background: { color: "transparent" },
        textColor: "#A79E93",
        fontFamily: "'JetBrains Mono', ui-monospace, monospace",
        fontSize: 11,
      },
      localization: { locale: "en-US" },
      rightPriceScale: {
        borderColor: "#3A2E22",
        scaleMargins: { top: 0.10, bottom: 0.28 },
      },
      timeScale: {
        borderColor: "#3A2E22",
        timeVisible: false,
        secondsVisible: false,
        fixLeftEdge: true,
        fixRightEdge: true,
      },
      grid: {
        vertLines: { color: "rgba(217,164,65,0.10)" },
        horzLines: { color: "rgba(217,164,65,0.10)" },
      },
      crosshair: {
        mode: CrosshairMode.Magnet,
        vertLine: { color: "#F26A1B", labelBackgroundColor: "#F26A1B", width: 1, style: 0 },
        horzLine: { color: "#F26A1B", labelBackgroundColor: "#F26A1B", width: 1, style: 0 },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale:  { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
    });

    let priceSeries;
    if (chartType === "line") {
      priceSeries = chart.addSeries(AreaSeries, {
        lineColor: "#F26A1B",
        topColor: "rgba(242,106,27,0.30)",
        bottomColor: "rgba(242,106,27,0.00)",
        lineWidth: 2,
        priceLineColor: "#F26A1B",
        priceLineWidth: 1,
        priceLineStyle: 2,
        lastValueVisible: true,
        priceFormat: { type: "price", precision: 4, minMove: 0.0001 },
      });
    } else {
      priceSeries = chart.addSeries(CandlestickSeries, {
        upColor: "#22C55E",
        downColor: "#EF4444",
        borderUpColor: "#22C55E",
        borderDownColor: "#EF4444",
        wickUpColor: "#22C55E",
        wickDownColor: "#EF4444",
        priceLineColor: "#F26A1B",
        priceLineWidth: 1,
        priceLineStyle: 2,
        priceFormat: { type: "price", precision: 4, minMove: 0.0001 },
      });
    }

    const volumeSeries = chart.addSeries(HistogramSeries, {
      priceFormat: { type: "volume" },
      priceScaleId: "volume",
      color: "rgba(242,106,27,0.35)",
    });
    chart.priceScale("volume").applyOptions({
      scaleMargins: { top: 0.78, bottom: 0 },
    });

    chartRef.current = chart;
    priceSeriesRef.current = priceSeries;
    volumeSeriesRef.current = volumeSeries;
    priceLineRef.current = null;

    // Crosshair → parent readout. Use param.time alone so it works with candles too.
    chart.subscribeCrosshairMove((param) => {
      if (!param || param.time === undefined || param.time === null) {
        const map = barsMapRef.current;
        if (map && map.size > 0) {
          let lastKey = null;
          for (const k of map.keys()) lastKey = k;
          if (lastKey) emit({ ...map.get(lastKey), isLast: true });
        } else {
          emit(null);
        }
        return;
      }
      const t = String(param.time);
      const map = barsMapRef.current;
      const bar = map.get(t) || null;
      if (!bar) {
        emit(null);
        return;
      }
      emit({ ...bar, isLast: false });
    });

    // Click / tap → parent pin/unpin. Dedupe rapid double-fires (StrictMode
    // dev re-runs the effect and lightweight-charts fires down+up as two
    // clicks on some builds — collapse anything within 200ms).
    let _lastClickAt = 0;
    chart.subscribeClick((param) => {
      const cb = clickCbRef.current;
      if (typeof cb !== "function") return;
      const now = Date.now();
      if (now - _lastClickAt < 200) return;
      _lastClickAt = now;
      if (!param || param.time === undefined || param.time === null) {
        cb(null);
        return;
      }
      const t = String(param.time);
      const bar = barsMapRef.current.get(t) || null;
      cb(bar);
    });

    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = Math.floor(entry.contentRect.width);
        if (w > 0) chart.applyOptions({ width: w });
      }
    });
    ro.observe(el);
    roRef.current = ro;

    return () => {
      try { ro.disconnect(); } catch (_) {}
      try { chart.remove(); } catch (_) {}
      chartRef.current = null;
      priceSeriesRef.current = null;
      volumeSeriesRef.current = null;
      priceLineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [height, chartType]);

  // Push data.
  useEffect(() => {
    const priceSeries = priceSeriesRef.current;
    const volumeSeries = volumeSeriesRef.current;
    if (!priceSeries || !volumeSeries) return;

    const clean = (bars || []).filter((b) => b && b.date && typeof b.close === "number");
    const priceData = clean.map((b) => {
      if (chartType === "line") return { time: b.date, value: b.close };
      const o = typeof b.open === "number" ? b.open : b.close;
      const h = typeof b.high === "number" ? b.high : b.close;
      const l = typeof b.low === "number" ? b.low : b.close;
      return { time: b.date, open: o, high: h, low: l, close: b.close };
    });
    priceSeries.setData(priceData);

    const volumeData = clean.map((b) => {
      const up = typeof b.open === "number" ? b.close >= b.open : true;
      return {
        time: b.date,
        value: typeof b.volume === "number" ? b.volume : 0,
        color: up ? "rgba(34,197,94,0.45)" : "rgba(239,68,68,0.45)",
      };
    });
    volumeSeries.setData(volumeData);

    const map = new Map();
    for (const b of clean) map.set(b.date, {
      date: b.date,
      open: typeof b.open === "number" ? b.open : null,
      high: typeof b.high === "number" ? b.high : null,
      low: typeof b.low === "number" ? b.low : null,
      close: b.close,
      volume: typeof b.volume === "number" ? b.volume : null,
    });
    barsMapRef.current = map;

    if (chartRef.current && clean.length > 0) {
      chartRef.current.timeScale().fitContent();
      const last = clean[clean.length - 1];
      emit({
        date: last.date,
        open: last.open ?? null,
        high: last.high ?? null,
        low: last.low ?? null,
        close: last.close,
        volume: typeof last.volume === "number" ? last.volume : null,
        isLast: true,
      });
    }
  }, [bars, chartType, emit]);

  // Persistent pin — draw / update / remove the horizontal price line.
  useEffect(() => {
    const priceSeries = priceSeriesRef.current;
    if (!priceSeries) return;
    // Remove any existing pin line
    if (priceLineRef.current) {
      try { priceSeries.removePriceLine(priceLineRef.current); } catch (_) {}
      priceLineRef.current = null;
    }
    if (!pinnedTime) return;
    const bar = barsMapRef.current.get(String(pinnedTime));
    if (!bar) return;
    try {
      priceLineRef.current = priceSeries.createPriceLine({
        price: bar.close,
        color: "#F26A1B",
        lineWidth: 2,
        lineStyle: 2, // dashed — visually distinct from the solid crosshair
        axisLabelVisible: true,
        title: "PIN",
      });
    } catch (_) {
      priceLineRef.current = null;
    }
  }, [pinnedTime, bars]);

  return (
    <div
      ref={containerRef}
      className="w-full relative"
      style={{ height, isolation: "isolate" }}
      data-testid="interactive-chart"
    />
  );
}
